import { ActivatedRoute, Router } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import {
  CaptureLevel1Types,
  CaptureSettingsMerchantTreeDto,
  CaptureValueType,
  CustomerEngagementTypes,
  DigitalReceipt_BusinessDetailsTypes,
  DigitalReceipt_ItemisedPurchaseListTypes,
  DigitalReceipt_PaymentMethodTypes,
  DigitalReceipt_TransactionDetailsTypes,
  DigitalReceiptTypes,
  DonationTypes,
  LeaveReviewTypes,
  MerchantFooterDetailsTypes,
  OrderItemDto,
  OrderReferenceTypes,
  ProcessingState,
  SalesPersonDetailsTypes
} from '@up/data';
import { OrderReceiptService } from '../../services/order-receipt.service';
import { SendInBlueService } from '../../services/send-in-blue.service';
import { emailRule } from '@up/shared/helpers';
import html2pdf from 'html2pdf.js';
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faTiktok,
  faYoutube,
  IconDefinition
} from '@fortawesome/free-brands-svg-icons';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { sampleOrder } from './order-receipt-sample-order.const';
import { SHOPPING_BASKET_SAMPLE_DATA } from '../live-cart/live-basket.const';
import { ClickEvent } from 'angular-star-rating';
import { CaptureSettingsMerchantApiService } from '../../services/capture-settings-merchant.api.service';
import { BehaviorSubject } from 'rxjs';
import { OrderCacheService } from '../../services/order-cache.service';

const HTML2PDF_OPTIONS = {
  html2canvas: {
    allowTaint: true,
    scale: 2,
    useCORS: true
  },
  margin: 1,
  filename: `Receipt.pdf`
};

const faXTwitter: IconDefinition = {
  prefix: 'fab',
  iconName: 'twitter',
  icon: [24, 24, [], '', 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z']
};

export interface DigitalReceiptConfig {
  businessDetails: {
    showBusinessName: boolean;
    showBusinessAddress: boolean;
    showContactPhone: boolean;
    showContactEmail: boolean;
    showTaxIdVat: boolean;
  };
  transactionDetails: {
    showDateOfPurchase: boolean;
    showTimeOfPurchase: boolean;
    showReceiptNumber: boolean;
    showTransactionNumber: boolean;
    showTransactionSource: boolean;
    showMerchantId: boolean;
    showTerminalId: boolean;
  };
  paymentMethod: {
    showPaymentMethod: boolean;
    showLast4Card: boolean;
  };
  itemisedPurchaseList: {
    showDescription: boolean;
    showQuantity: boolean;
    showUnitPrice: boolean;
    showTotalPerLineItem: boolean;
    showSubtotalBeforeTax: boolean;
    showTaxesWithRate: boolean;
    showDiscounts: boolean;
    showFinalAmountPayable: boolean;
  };
  showSalesPersonNameId: boolean;
  showLoyaltyMembershipDetails: boolean;
  showReturnsRefundPolicyStatement: boolean;
  orderReference: {
    orderReference: boolean;
    orderNumbers: boolean;
    humanFriendlyOrderReference: boolean;
  };
  merchantFooter: boolean;
  merchantFooterDetails: {
    showMerchantLogo: boolean;
    showMerchantName: boolean;
    showMerchantAddress: boolean;
    showMerchantContactPhone: boolean;
    showMerchantContactEmail: boolean;
    showMerchantWebsite: boolean;
    showMerchantSocialMediaLinks: boolean;
  };
  salesPersonDetails: {
    salesPersonDetails: boolean;
    hrSuiteProvider: string;
    defaultSalesPersonImage: string;
    defaultSalesPersonMessage: string;
    allowImageFromEmployeeProfile: boolean;
    allowMessageFromEmployeeProfile: boolean;
    showSalesPersonRating: boolean;
    leaveTip: boolean;
  };
  customerEngagement: {
    customerRegistration: boolean;
    addIncentiveToRegister: boolean;
    registerIncentive: string;
    loyaltyProgramme: boolean;
    addIncentiveToJoinLoyalty: boolean;
    loyaltyIncentive: string;
    giftCardUpsell: boolean;
    downloadApp: boolean;
  };
  leaveReview: {
    leaveReview: boolean;
    netPromoterScore: boolean;
    customerSatisfaction: boolean;
  };
  donation: {
    donation: boolean;
    charityPartner: string;
  };
}

@Component({
  selector: 'up-order-receipt',
  templateUrl: './order-receipt.component.html',
  styleUrls: ['./order-receipt.component.scss'],
  animations: [
    trigger('cardAnimation', [
      state('visible', style({
        opacity: 1,
        transform: 'scale(1)',
        height: '*'
      })),
      state('hidden', style({
        opacity: 0,
        transform: 'scale(0.95)',
        height: '0px',
        overflow: 'hidden'
      })),
      transition('visible => hidden', [
        animate('0.5s cubic-bezier(0.35, 0, 0.25, 1)')
      ]),
      transition('hidden => visible', [
        animate('0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)')
      ])
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden'
        }),
        animate('0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)', style({
          height: '*',
          opacity: 1
        }))
      ])
    ])
  ]
})
export class OrderReceiptComponent implements OnInit {
   salesTaxPercent = 6.5;
   salesTaxValue: number;
   total: number;
   showLeaveTip = false;
   currentDate = new Date();

  constructor(
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private router: Router,
    private orderReceiptService: OrderReceiptService,
    private sendInBlueService: SendInBlueService,
    private settingsMerchantService: CaptureSettingsMerchantApiService,
    private orderCacheService: OrderCacheService,
  ) {
    const params = this.route.snapshot.queryParams;
    this.merchantLink = params.merchantLink;
    this.orderLink = params.orderLink;
    this.gcQty = params.gcQty || '1';
    this.gcAmount = Number(params.GcAmount);
    this.splitPayments = Number(params.splitPayments) || 1;
    this.giftCardUrl = `https://store.crave.cards/card-manager/issue?id=${this.merchantLink}&showNavigation=true`;
  }


  _configuration: DigitalReceiptConfig = {
    businessDetails: {
      showBusinessName: true,
      showBusinessAddress: true,
      showContactPhone: true,
      showContactEmail: true,
      showTaxIdVat: true,
    },
    transactionDetails: {
      showDateOfPurchase: true,
      showTimeOfPurchase: true,
      showReceiptNumber: true,
      showTransactionNumber: false,
      showTransactionSource: true,
      showMerchantId: false,
      showTerminalId: false,
    },
    paymentMethod: {
      showPaymentMethod: true,
      showLast4Card: true,
    },
    itemisedPurchaseList: {
      showDescription: true,
      showQuantity: true,
      showUnitPrice: true,
      showTotalPerLineItem: true,
      showSubtotalBeforeTax: true,
      showTaxesWithRate: true,
      showDiscounts: true,
      showFinalAmountPayable: true,
    },
    showSalesPersonNameId: false,
    showLoyaltyMembershipDetails: true,
    showReturnsRefundPolicyStatement: true,
    orderReference: {
      orderReference: true,
      orderNumbers: true,
      humanFriendlyOrderReference: false,
    },
    merchantFooter: true,
    merchantFooterDetails: {
      showMerchantLogo: true,
      showMerchantName: true,
      showMerchantAddress: true,
      showMerchantContactPhone: true,
      showMerchantContactEmail: true,
      showMerchantWebsite: true,
      showMerchantSocialMediaLinks: true,
    },
    salesPersonDetails: {
      salesPersonDetails: false,
      hrSuiteProvider: null,
      defaultSalesPersonImage: null,
      defaultSalesPersonMessage: null,
      allowImageFromEmployeeProfile: false,
      allowMessageFromEmployeeProfile: false,
      showSalesPersonRating: false,
      leaveTip: false,
    },
    customerEngagement: {
      customerRegistration: false,
      addIncentiveToRegister: true,
      registerIncentive: null,
      loyaltyProgramme: false,
      addIncentiveToJoinLoyalty: true,
      loyaltyIncentive: null,
      giftCardUpsell: false,
      downloadApp: false,
    },
    leaveReview: {
      leaveReview: false,
      netPromoterScore: true,
      customerSatisfaction: false,
    },
    donation: {
      donation: false,
      charityPartner: null,
    },
  };

  @ViewChild('customAmountSelect') customAmountSelect: any;
  liveBasketSampleProducts = SHOPPING_BASKET_SAMPLE_DATA;
  processedConfiguration$ = new BehaviorSubject<DigitalReceiptConfig>(this._configuration);
  orderNotProcessed = true;

  public subTotal = 0;
  public totalDrs = 0;

  public order: any;
  public merchantLink: string;
  public orderLink: string;
  public gcQty: string;
  public gcAmount: number;
  public splitPayments: number;
  public vatRatesMap: Map<string, number>;
  public loading = true;
  public sendReceipt = false;
  public emailAddress = '';
  public receiptSent = false;
  public isSendingEmail = false;
  public isSavingPDF = false;
  public shouldSaveReceipt = false;
  public employeeAnimationState = 'visible';
  public notificationAnimationState = 'hidden';
  public showTipOptions = false;
  public showTipPaymentSection = false;
  public emailRegex = emailRule;
  public selectedTipAmount: number | null = null;
  public customTipAmount: number | null = null;
  public employeeRating = 0;
  public amountOptions = [
    { label: '€5', value: 5 },
    { label: '€10', value: 10 },
    { label: '€20', value: 20 },
    { label: '€50', value: 50 },
    { label: '€100', value: 100 }
  ];
  title = 'web-order-receipt';
  googleWalletLink = 'javascript:void(0)';
  appleWalletLink = 'javascript:void(0)';
  giftCardUrl = `https://www.louisfitzgerald.com/giftcards/`;

  async ngOnInit() {
    await this.initialize();

    this.settingsMerchantService.getCoreFunctionalitySettingsTreeByMerchantIdentifier(this.merchantLink, 'digital-receipt')
        .subscribe(c => {
          if (!c) {
            return;
          }
          const config = (c as CaptureSettingsMerchantTreeDto[]);
          // preselect first item of the first level and second level
          if (config.length) {
            if (config[0].children.length) {
              const selectedItemLevel1 = config[0];
              this.processConfig(selectedItemLevel1);
              this.processedConfiguration$.next(this._configuration);
            }
          }
        });

    setTimeout(() => {
      /** spinner ends after 5 seconds */
      this.employeeRating = 1;
    }, 10);
  }

  async initialize() {
    if (this.orderLink === 'mo0d2fyiay') {
      this.order = sampleOrder;

      this.subTotal = this.getSubTotal();
      this.salesTaxValue = this.getSalesTaxValue(this.salesTaxPercent);
      this.total = this.getTotal();

      if(this.gcAmount) {
        this.total += this.gcAmount;
      }
      this.orderNotProcessed = false;
      this.loading = false;
    }
    else if (this.merchantLink && this.orderLink) {
      await Promise.all([
        this.orderCacheService.getByOrderLink(this.orderLink).toPromise().catch(() => null),
        this.orderReceiptService.getByOrderLinkAndMerchantLink(this.orderLink, this.merchantLink).toPromise().catch(() => null)
      ])
        .then(
          async ([orderCache, o]) => {
            this.loading = false;
            if (o) {
              this.orderNotProcessed = false;
              this.order = o;

              this.orderReceiptService.getWalletUrls(o.id).toPromise()
                .then(async (urls) => {
                  this.googleWalletLink = urls.googleWalletLink;
                  this.appleWalletLink = urls.appleWalletLink;
                });
              this.calculateSubtotalAndVAT(this.order?.orderItems);

              return;
            }

            if (orderCache && orderCache.processingState !== ProcessingState.PROCESSED) {
              this.orderNotProcessed = true;

              return;
            }

            this.orderNotProcessed = false;
            this.router.navigate(['/error']);
          }
        );
    }
  }

  getSubTotal(): number {
    return this.liveBasketSampleProducts.reduce((acc, item) => acc + item.total, 0);
  }

  getSalesTaxValue(_vatPercent = this.salesTaxPercent): number {
    return this.getSubTotal() * _vatPercent / 100;
  }

  getTotal(): number {
    return this.getSubTotal() + this.getSalesTaxValue(this.salesTaxPercent);
  }

  get totalTipsAmount(): number {
    return this.order?.orderPayments?.reduce((acc: number, payment: any) => acc + (payment?.tipsAmount || 0), 0) || 0;
  }

  private calculateSubtotalAndVAT(orderItems: OrderItemDto[]) {
    this.vatRatesMap = new Map();
    const vatRateList = orderItems.map((x) => x.vatRate);
    // Alter VAT Rates List to use the right VAT amount
    vatRateList.forEach((vatRate) => {
      if (vatRate) {
        const vatRateValue = vatRate.vatRateValue
          ?.filter((vrv) => new Date(vrv.effectiveFrom) <= new Date())
          ?.sort(this.compareVatRates)[0];
        vatRate.vatRateValue = [vatRateValue];
      }
    });

    // Create a map to store VAT class and VAT value
    if (vatRateList.length > 0) {
      vatRateList.forEach((vatRate) => {
        if (vatRate) {
          this.vatRatesMap.set(
            vatRate.vatRateValue[0]?.amount + '%',
            0
          );
        }
      });
    }

    orderItems.forEach((orderItem) => {
      const totalItemPrice =
        (orderItem.price + orderItem.modifierTotal) * orderItem.quantity;
      this.subTotal += totalItemPrice;
      this.totalDrs += orderItem.drsValue * orderItem.quantity;

      if (orderItem.bundleCombinationId) {
        const bundle = orderItems.find(
          (oi) =>
            oi.bundleCombinationId === orderItem.bundleCombinationId &&
            oi.product.isBundle === true
        );

        const existingDiscount = orderItem.discountAmount || 0;
        const bundleComponentsTotalPrice = orderItems
          .filter(
            (oi) =>
              oi.bundleCombinationId === orderItem.bundleCombinationId &&
              oi.product.isBundle === false
          )
          .map((oi) => (oi.price + oi.modifierTotal) * oi.quantity)
          .reduce((a, b) => a + b, 0);

        orderItem.discountAmount =
          existingDiscount +
          -(
            (bundle.price *
              (totalItemPrice - orderItem.discountAmount) *
              bundle.quantity) /
            bundleComponentsTotalPrice
          );
      }

      const vatRate = vatRateList.find((vr) => vr?.id === orderItem.vatRateId);
      if (vatRate) {
        this.vatRatesMap.set(
          vatRate.vatRateValue[0]?.amount + '%',
          this.vatRatesMap.get(
            vatRate.vatRateValue[0]?.amount + '%'
          ) +
            ((orderItem.quantity * (orderItem.price + orderItem.modifierTotal) -
              orderItem.discountAmount) *
              vatRate.vatRateValue[0]?.amount) /
              (100 + vatRate.vatRateValue[0]?.amount)
        );
      }
    });
  }

  saveAsPDF() {
    this.isSavingPDF = true;

    const element = document.getElementById('pdf-content');
    html2pdf()
      .from(element)
      .set(HTML2PDF_OPTIONS)
      .save()
      .then(() => {
        this.isSavingPDF = false;
        this.ref.detectChanges();
      });
  }

  toggleDownloadSection(): void {
    this.shouldSaveReceipt = !this.shouldSaveReceipt;
  }

  toggleEmployeeSection(): void {
    if (this.employeeAnimationState === 'visible') {
      this.employeeAnimationState = 'hidden';
      this.showTipOptions = false;
    } else {
      this.employeeAnimationState = 'visible';
    }
  }

  toggleReceiptSentNotification() {
    if (this.notificationAnimationState === 'visible') {
      this.notificationAnimationState = 'hidden';
      this.receiptSent = false;
    } else {
      this.notificationAnimationState = 'visible';
      this.receiptSent = true;
    }
  }

  leaveTip() {
    this.showTipOptions = true;
  }

  tipSelected(amount: number) {
    this.selectedTipAmount = amount;
    this.showTipPaymentSection = true;
    this.customAmountSelect?.element?.classList?.remove('ng-dirty');
    // this.customAmountSelect.clearModel();
  }

  customTipSelected(amount: number) {
    this.selectedTipAmount = amount;
    this.customTipAmount = amount;
  }

  async sendEmail() {
    this.isSendingEmail = true;

    const element = document.getElementById('pdf-content');
    html2pdf()
      .from(element)
      .set(HTML2PDF_OPTIONS)
      .toPdf()
      .output('datauristring')
      .then(async (pdfAsString: string) => {
        this.sendInBlueService
          .sendEmail(pdfAsString.split(',')[1], this.emailAddress)
          .subscribe((_) => {
            this.toggleReceiptSentNotification();
            this.toggleDownloadSection();
            this.isSendingEmail = false;
            this.sendReceipt = false;
            this.ref.detectChanges();
            window.scrollTo(0, 0);
          });
      });
  }

  getRandomCardNumbers(showLast4Card: boolean): string {
    const paymentText = 'VISA';
    return showLast4Card ? paymentText + ' **** ' + Math.floor(Math.random() * 10000).toString().padStart(4, '0') : paymentText;
  }

  getPaymentDisplayText(showLast4Card: boolean, payment: any): string {
    if (!payment?.data) {
      return payment.message || '';
    }

    let cardType = '';
    let last4 = '';

    // Original format
    if (payment?.data?.cardTypeCode) {
      cardType = payment?.data?.cardTypeCode;
      last4 = payment?.data?.last4;
    }
    // Stripe format
    else if (payment?.data?.paymentMethod?.card) {
      cardType = payment?.data?.paymentMethod.card.brand.charAt(0).toUpperCase() + payment?.data?.paymentMethod.card.brand.slice(1);
      last4 = payment?.data?.paymentMethod.card.last4;
    }
    // AIB format
    else if (payment?.data?.cardNumber) {
      cardType = payment?.data?.cardType.charAt(0).toUpperCase() + payment?.data?.cardType.slice(1);
      last4 = payment?.data?.cardNumber.slice(-4);
    }
    else {
      return payment.message || '';
    }

    return showLast4Card ? `${cardType} **** ${last4}` : cardType;
  }

  getArray(num: number) {
    return Array(num).fill(0);
  }

  getDeviceType(): 'android' | 'ios' | 'other' {
    const ua = navigator.userAgent.toLowerCase();
    if (/android/.test(ua)) return 'android';
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    return 'other';
  }

  compareVatRates(a, b) {
    if (a.effectiveFrom > b.effectiveFrom) {
      return -1;
    }
    if (a.effectiveFrom < b.effectiveFrom) {
      return 1;
    }
    return 0;
  }

  getSocialIcon(socialName: string) {
    const iconMap: { [key: string]: IconDefinition } = {
      'Facebook': faFacebookF,
      'Instagram': faInstagram,
      'Twitter': faXTwitter,
      'X': faXTwitter,
      'LinkedIn': faLinkedinIn,
      'YouTube': faYoutube,
      'TikTok': faTiktok
    };

    return iconMap[socialName] || faLink;
  }

  getRoundedTip(percentage: number): number {
    if (!this.order?.totalPrice) return 0;
    return Math.round(this.order.totalPrice * percentage * 10) / 10;
  }

  onStarClicked($event: ClickEvent) {
      if ($event.rating === 5) {
        this.showLeaveTip = true
      }
  }


  processConfig(config: CaptureSettingsMerchantTreeDto) {
    const flattedConfig = this.flattenSettingsTree<CaptureSettingsMerchantTreeDto>(config.children);

    flattedConfig.forEach(node => {
      switch (node.key) {

          // Business Details
        case DigitalReceipt_BusinessDetailsTypes.SHOW_BUSINESS_NAME:
          this._configuration.businessDetails.showBusinessName = this.getTypedValue(node);
          break;
        case DigitalReceipt_BusinessDetailsTypes.SHOW_BUSINESS_ADDRESS:
          this._configuration.businessDetails.showBusinessAddress = this.getTypedValue(node);
          break;
        case DigitalReceipt_BusinessDetailsTypes.SHOW_CONTACT_PHONE:
          this._configuration.businessDetails.showContactPhone = this.getTypedValue(node);
          break;
        case DigitalReceipt_BusinessDetailsTypes.SHOW_CONTACT_EMAIL:
          this._configuration.businessDetails.showContactEmail = this.getTypedValue(node);
          break;
        case DigitalReceipt_BusinessDetailsTypes.SHOW_TAX_ID_VAT:
          this._configuration.businessDetails.showTaxIdVat = this.getTypedValue(node);
          break;

          // Transaction Details
        case DigitalReceipt_TransactionDetailsTypes.SHOW_DATE_OF_PURCHASE:
          this._configuration.transactionDetails.showDateOfPurchase = this.getTypedValue(node);
          break;
        case DigitalReceipt_TransactionDetailsTypes.SHOW_TIME_OF_PURCHASE:
          this._configuration.transactionDetails.showTimeOfPurchase = this.getTypedValue(node);
          break;
        case DigitalReceipt_TransactionDetailsTypes.SHOW_RECEIPT_NUMBER:
          this._configuration.transactionDetails.showReceiptNumber = this.getTypedValue(node);
          break;
        case DigitalReceipt_TransactionDetailsTypes.SHOW_TRANSACTION_NUMBER:
          this._configuration.transactionDetails.showTransactionNumber = this.getTypedValue(node);
          break;
        case DigitalReceipt_TransactionDetailsTypes.SHOW_TRANSACTION_SOURCE:
          this._configuration.transactionDetails.showTransactionSource = this.getTypedValue(node);
          break;
        case DigitalReceipt_TransactionDetailsTypes.SHOW_MERCHANT_ID:
          this._configuration.transactionDetails.showMerchantId = this.getTypedValue(node);
          break;
        case DigitalReceipt_TransactionDetailsTypes.SHOW_TERMINAL_ID:
          this._configuration.transactionDetails.showTerminalId = this.getTypedValue(node);
          break;

          // Payment Method
        case DigitalReceipt_PaymentMethodTypes.SHOW_PAYMENT_METHOD:
          this._configuration.paymentMethod.showPaymentMethod = this.getTypedValue(node);
          break;
        case DigitalReceipt_PaymentMethodTypes.SHOW_LAST4_CARD:
          this._configuration.paymentMethod.showLast4Card = this.getTypedValue(node);
          break;

          // Itemised Purchase List
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_DESCRIPTION:
          this._configuration.itemisedPurchaseList.showDescription = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_QUANTITY:
          this._configuration.itemisedPurchaseList.showQuantity = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_UNIT_PRICE:
          this._configuration.itemisedPurchaseList.showUnitPrice = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_TOTAL_PER_LINE_ITEM:
          this._configuration.itemisedPurchaseList.showTotalPerLineItem = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_SUBTOTAL_BEFORE_TAX:
          this._configuration.itemisedPurchaseList.showSubtotalBeforeTax = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_TAXES_WITH_RATE:
          this._configuration.itemisedPurchaseList.showTaxesWithRate = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_DISCOUNTS:
          this._configuration.itemisedPurchaseList.showDiscounts = this.getTypedValue(node);
          break;
        case DigitalReceipt_ItemisedPurchaseListTypes.SHOW_FINAL_AMOUNT_PAYABLE:
          this._configuration.itemisedPurchaseList.showFinalAmountPayable = this.getTypedValue(node);
          break;
        case DigitalReceiptTypes.SHOW_SALES_PERSON_NAME_ID:
          this._configuration.showSalesPersonNameId = this.getTypedValue(node);
          break;
        case DigitalReceiptTypes.SHOW_LOYALTY_MEMBERSHIP_DETAILS:
          this._configuration.showLoyaltyMembershipDetails = this.getTypedValue(node);
          break;
        case DigitalReceiptTypes.SHOW_RETURNS_REFUND_POLICY_STATEMENT:
          this._configuration.showReturnsRefundPolicyStatement = this.getTypedValue(node);
          break;

          // Order Reference
        case CaptureLevel1Types.ORDER_REFERENCE:
          this._configuration.orderReference.orderReference = this.getTypedValue(node);
          break;
        case OrderReferenceTypes.ORDER_NUMBERS:
          this._configuration.orderReference.orderNumbers = this.getTypedValue(node);
          break;
        case OrderReferenceTypes.HUMAN_FRIENDLY_ORDER_REFERENCE:
          this._configuration.orderReference.humanFriendlyOrderReference = this.getTypedValue(node);
          break;

          // Sales Person Details
        case CaptureLevel1Types.SALES_PERSON_DETAILS:
          this._configuration.salesPersonDetails.salesPersonDetails = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.HR_SUITE_PROVIDER:
          this._configuration.salesPersonDetails.hrSuiteProvider = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.DEFAULT_SALES_PERSON_IMAGE:
          this._configuration.salesPersonDetails.defaultSalesPersonImage = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.DEFAULT_SALES_PERSON_MESSAGE:
          this._configuration.salesPersonDetails.defaultSalesPersonMessage = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.ALLOW_IMAGE_FROM_EMPLOYEE_PROFILE:
          this._configuration.salesPersonDetails.allowImageFromEmployeeProfile = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.ALLOW_MESSAGE_FROM_EMPLOYEE_PROFILE:
          this._configuration.salesPersonDetails.allowMessageFromEmployeeProfile = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.SHOW_SALES_PERSON_RATING:
          this._configuration.salesPersonDetails.showSalesPersonRating = this.getTypedValue(node);
          break;
        case SalesPersonDetailsTypes.SALES_PERSON_DETAILS_LEAVE_TIP:
          this._configuration.salesPersonDetails.leaveTip = this.getTypedValue(node);
          break;

          // Customer Engagement
        case CustomerEngagementTypes.CUSTOMER_REGISTRATION:
          this._configuration.customerEngagement.customerRegistration = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.ADD_INCENTIVE_TO_REGISTER:
          this._configuration.customerEngagement.addIncentiveToRegister = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.REGISTER_INCENTIVE:
          this._configuration.customerEngagement.registerIncentive = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.LOYALTY_PROGRAMME:
          this._configuration.customerEngagement.loyaltyProgramme = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.ADD_INCENTIVE_TO_JOIN_LOYALTY:
          this._configuration.customerEngagement.addIncentiveToJoinLoyalty = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.LOYALTY_INCENTIVE:
          this._configuration.customerEngagement.loyaltyIncentive = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.GIFT_CARD_UPSELL:
          this._configuration.customerEngagement.giftCardUpsell = this.getTypedValue(node);
          break;
        case CustomerEngagementTypes.DOWNLOAD_APP:
          this._configuration.customerEngagement.downloadApp = this.getTypedValue(node);
          break;

        case CaptureLevel1Types.DONATION:
          this._configuration.donation.donation = this.getTypedValue(node);
          break;
        case DonationTypes.CHARITY_PARTNER:
          this._configuration.donation.charityPartner = this.getTypedValue(node);
          break;

          // Leave Review
        case CaptureLevel1Types.LEAVE_REVIEW:
          this._configuration.leaveReview.leaveReview = this.getTypedValue(node);
          break;
        case LeaveReviewTypes.NET_PROMOTER_SCORE:
          this._configuration.leaveReview.netPromoterScore = this.getTypedValue(node);
          break;
        case LeaveReviewTypes.CUSTOMER_SATISFACTION:
          this._configuration.leaveReview.customerSatisfaction = this.getTypedValue(node);
          break;

          // Merchant Footer Details
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_LOGO:
          this._configuration.merchantFooterDetails.showMerchantLogo = this.getTypedValue(node);
          break;
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_NAME:
          this._configuration.merchantFooterDetails.showMerchantName = this.getTypedValue(node);
          break;
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_ADDRESS:
          this._configuration.merchantFooterDetails.showMerchantAddress = this.getTypedValue(node);
          break;
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_CONTACT_PHONE:
          this._configuration.merchantFooterDetails.showMerchantContactPhone = this.getTypedValue(node);
          break;
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_CONTACT_EMAIL:
          this._configuration.merchantFooterDetails.showMerchantContactEmail = this.getTypedValue(node);
          break;
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_WEBSITE:
          this._configuration.merchantFooterDetails.showMerchantWebsite = this.getTypedValue(node);
          break;
        case MerchantFooterDetailsTypes.SHOW_MERCHANT_SOCIAL_MEDIA_LINKS:
          this._configuration.merchantFooterDetails.showMerchantSocialMediaLinks = this.getTypedValue(node);
          break;
      }
    })
  }

  getTypedValue(node: CaptureSettingsMerchantTreeDto): any {
    const nodeValue = node.merchantSettingValue ?? node.defaultValue;
    if (node.valueType === CaptureValueType.BOOLEAN) {
      if (typeof nodeValue === 'string') {
        return nodeValue.toLowerCase() === 'true' || nodeValue === '1';
      }
      return Boolean(nodeValue);
    }
    return nodeValue;
  }

  flattenSettingsTree<T>(nodes: T[]): T[] {
    const flat: T[] = [];
    const stack: T[] = [ ...nodes ];

    while (stack.length > 0) {
      const node = stack.pop();
      if (node) {
        flat.push(node);
        if (node['children'] && node['children'].length > 0) {
          stack.push(...node['children']);
        }
      }
    }
    return flat;
  }

  downloadApp() {
    window.open('https://apps.apple.com/us/app/supersweet-people/id6618156926', '_blank');
  }

  getCurrencySymbol(currencyCode: string): string {
    const currencies = {
      '036': 'AUD', '124': 'CAD', '156': 'CNY', '203': 'CZK',
      '208': 'DKK', '344': 'HKD', '348': 'HUF', '356': 'INR',
      '392': 'JPY', '410': 'KRW', '484': 'MXN', '554': 'NZD',
      '578': 'NOK', '702': 'SGD', '710': 'ZAR', '752': 'SEK',
      '756': 'CHF', '826': 'GBP', '840': 'USD', '978': 'EUR',
      '986': 'BRL', '985': 'PLN', '949': 'TRY', '682': 'SAR',
      '784': 'AED', '196': 'CYP', '191': 'HRK', '643': 'RUB'
    };
    return currencies[currencyCode] || currencyCode;
  }
}
